<template>
  <PluginItemDropdown
    id="transform"
    :title="$t('transformTo', 'Other actions')"
    :enabled="!!(itemBundleIds.length && possibleTransforms.length)"
  >
    <button
      v-for="transform in possibleTransforms"
      :key="transform.id"
      @click.prevent="onTransform(transform, selection.uuids.value)"
    >
      <div>
        <div>{{ transform.label }}</div>
      </div>
    </button>
  </PluginItemDropdown>
</template>

<script setup>
import {
  computed,
  watch,
  useBlokkli,
  defineBlokkliFeature,
  useLazyAsyncData
} from "#imports";
import { PluginItemDropdown } from "#blokkli/plugins";
import { onlyUnique } from "#blokkli/helpers";
import { filterTransforms } from "#blokkli/helpers/transform";
import defineCommands from "#blokkli/helpers/composables/defineCommands";
import defineDropAreas from "#blokkli/helpers/composables/defineDropAreas";
const { adapter } = defineBlokkliFeature({
  id: "transform",
  icon: "script",
  label: "Transform",
  requiredAdapterMethods: ["getTransformPlugins", "applyTransformPlugin"],
  description: "Provides integration for block transform plugins.",
  screenshot: "feature-transform.jpg"
});
const { types, selection, state, $t, dom, ui } = useBlokkli();
const {
  data: plugins,
  execute,
  status
} = await useLazyAsyncData(
  () => {
    return adapter.getTransformPlugins();
  },
  { immediate: false, default: () => [] }
);
watch(selection.uuids, async () => {
  if (status.value === "idle") {
    execute();
  }
});
async function onTransform(plugin, uuids) {
  ui.setTransform(plugin.label);
  await state.mutateWithLoadingState(
    () => adapter.applyTransformPlugin({
      uuids,
      pluginId: plugin.id
    }),
    $t(
      "failedToTransform",
      'The action "@name" could not be executed.'
    ).replace("@name", plugin.label)
  );
  ui.setTransform();
}
const itemBundleIds = computed(
  () => selection.blocks.value.map((v) => v.itemBundle).filter(onlyUnique)
);
const possibleTransforms = computed(
  () => filterTransforms(
    plugins.value || [],
    selection.uuids.value,
    itemBundleIds.value,
    types.allowedTypesInList.value
  )
);
defineCommands(
  () => possibleTransforms.value.map((transform) => ({
    id: "transform:" + transform.id,
    label: transform.label,
    group: "selection",
    icon: "script",
    callback: () => onTransform(transform, selection.uuids.value)
  }))
);
const getPossibleDropTransforms = (plugins2, allBlocks, dragItems) => {
  const notDraggedBlocks = allBlocks.filter(
    (block) => !dragItems.find((dragItem) => dragItem.uuid === block.uuid)
  );
  const validPlugins = plugins2.filter((plugin) => {
    const draggedBundles = dragItems.map((item) => item.itemBundle);
    return draggedBundles.every((bundle) => plugin.bundles.includes(bundle)) && dragItems.length + 1 >= plugin.min && (plugin.max === -1 || dragItems.length + 1 <= plugin.max);
  });
  const possibleTransforms2 = [];
  validPlugins.forEach((plugin) => {
    notDraggedBlocks.forEach((block) => {
      if (plugin.targetBundles.includes(block.itemBundle)) {
        possibleTransforms2.push({
          plugin,
          block
        });
      }
    });
  });
  return possibleTransforms2;
};
defineDropAreas((dragItems) => {
  const existing = dragItems.filter(
    (v) => v.itemType === "existing"
  );
  if (!existing.length) {
    return;
  }
  const uuids = existing.map((v) => v.uuid);
  return getPossibleDropTransforms(
    plugins.value,
    dom.getAllBlocks(),
    existing
  ).map((v) => {
    return {
      id: `transform:${v.plugin.id}:${v.block.uuid}`,
      label: v.plugin.label,
      element: v.block.element(),
      onDrop: () => {
        const transformUuids = [v.block.uuid, ...uuids];
        return onTransform(v.plugin, transformUuids);
      }
    };
  });
});
</script>

<script>
export default {
  name: "Transform"
};
</script>
